import Button from "@mui/material/Button";
import React, { useContext, useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthAPI } from "../../api/AuthAPI";
import { AuthContext } from "./AuthContext";
import otpImg from '../../assets/images/otp.png';
import ReCAPTCHA from "react-google-recaptcha";

// Custom VerificationInput with paste support
const VerificationInput = ({ length = 6, onChange, autoFocus = false }) => {
  const [values, setValues] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);
    
    // Call onChange with the full code
    const fullCode = newValues.join('');
    onChange(fullCode);

    // Auto-focus next input on entry
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    // Get pasted data and limit to length
    const pasteData = e.clipboardData.getData('text').slice(0, length);
    const pasteArray = pasteData.split('');
    
    const newValues = [...values];
    pasteArray.forEach((char, index) => {
      if (index < length && char.match(/[0-9]/)) { // Only allow digits
        newValues[index] = char;
      }
    });
    
    setValues(newValues);
    const fullCode = newValues.join('');
    onChange(fullCode);
    
    // Focus the next empty input or the last one
    const lastFilledIndex = newValues.findIndex(val => val === '');
    const focusIndex = lastFilledIndex === -1 ? length - 1 : Math.max(0, lastFilledIndex);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div 
      style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}
      onPaste={handlePaste}
    >
      {values.map((value, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          maxLength={1}
          value={value}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          autoFocus={autoFocus && index === 0}
          style={{
            width: '50px',
            height: '60px',
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: '500',
            border: value ? '2px solid #1976d2' : '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            backgroundColor: value ? '#f0f7ff' : 'white',
            transition: 'all 0.2s ease',
            color: '#1a1a1a'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#1976d2';
            e.target.style.boxShadow = '0 0 0 3px rgba(25, 118, 210, 0.15)';
            e.target.style.backgroundColor = '#f8faff';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = value ? '#1976d2' : '#ddd';
            e.target.style.boxShadow = 'none';
            e.target.style.backgroundColor = value ? '#f0f7ff' : 'white';
          }}
        />
      ))}
    </div>
  );
};

const MemberVerification = () => {
  const { signIn, isLoading, setMessage, setIsLoading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [code, setCode] = useState("");
  
  let stateObj = location?.state;

  if (!stateObj) {
    navigate("/login");
    return null;
  }

  const handleCode = (value) => {
    setCode(value);
  };

  const login = async () => {
    await signIn(stateObj?.userName?.trim(), stateObj?.password?.trim());
    if (stateObj?.token) {
      navigate("/events");
    } else {
      navigate("/");
    }
  };

  const handleActivateMember = async (e) => {
    e.preventDefault();
    setMessage({ type: '', content: '' });
    const currentUser = stateObj?.userName;

    if (!code || code?.length < 6) {
      setMessage({ type: 'error', content: 'Please enter the 6-digit verification code' });
      return;
    }

    if (!captchaVerified) {
      setMessage({ type: 'error', content: 'Please complete the CAPTCHA verification' });
      return;
    }

    try {
      const confirmData = {
        token: code,
        userName: currentUser
      };
      setIsLoading(true);
      await AuthAPI.confirmSignUp(confirmData);
      setIsLoading(false);
      setMessage({ type: 'success', content: 'Your email is verified. Please log in' });
      
      if (stateObj?.token) {
        login();
      } else {
        navigate("/login", { replace: true });
      }
    } catch (err) {
      setIsLoading(false);
      setMessage({ type: 'error', content: err?.response?.data?.Data || 'Verification failed' });
    }
  };

  const verifyCaptcha = async (data) => {
    try {
      const response = await AuthAPI.postVerifyCaptcha(data);
      setCaptchaVerified(response.Data);
    } catch (error) {
      setCaptchaVerified(false);
    }
  };

  return (
    <div className="otp_verification_container">
      <div className="otp_verification_field">
        <img src={otpImg} alt="OTP Verification" />
        <h2>Verify Your Email</h2>
        <p>
          A confirmation code has been sent to your email address <span>{stateObj?.userName}</span>. 
          To activate the account and validate your email, enter the code below.
        </p>
        <form className="otp_validate_input" noValidate>
          <VerificationInput
            autoFocus={true}
            length={6}
            onChange={handleCode}
          />
          
          <ReCAPTCHA
            style={{ alignSelf: 'center', marginTop: '15px' }}
            className="re-captcha"
            sitekey={`${import.meta.env.VITE_APP_CAPTCHA_SITE_KEY}`}
            onChange={verifyCaptcha}
          />
          
          <div className="otp_validate_btn">
            <Button
              type="submit"
              fullWidth
              disabled={!captchaVerified || isLoading}
              variant="contained"
              onClick={handleActivateMember}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberVerification;